/*
The Application belongs to Comprehend PT Inc.
    Author: Jacob Michalski
    Date Created: 09/18/2026
*/
// Is the onboarding checklist the thing guiding this user right now? Shared by
// the checklist itself and by the Appcues bootstrap: while the checklist is up
// (a post-launch account, or `force_onboarding_checklist`), Appcues' welcome
// flow stays out of the way — one guide at a time.
// No imports on purpose: api/Index.js pulls this in, and the component pulls
// in the api, so this file is the cycle-free middle.

// Advanced features is "done" at a VERSION, per EMR — bump `default` (or add
// an EMR key) alongside the Playground's ADV_VERSION to pull finished accounts
// back to 4 of 5 when a feature worth re-surfacing ships.
// Playground Magic counts as done when its three lessons are, even if the
// module flag itself never landed (a reset or a lost write on the Playground).
export const basicDone = (o) => !!(o && (o.basic || (o.lesson_record && o.lesson_learn && o.lesson_ia)));

export const ADVANCED_VERSION = { default: 1 };
export function emrKey(apiClient) {
  return String((apiClient && apiClient.EMR) || '').toLowerCase().trim().replace(/\s*\(.*\)\s*$/, '').replace(/[^a-z0-9]+/g, '-') || 'default';
}
// The version they finished Advanced features at: their own EMR's copy first,
// else the best of any EMR's — the lessons are the same features, so a Stride
// run counts for a Raintree account (opened from a link without the EMR, or
// an EMR setting that changed since). The legacy boolean is v1.
export function advancedFinishedAt(o, k) {
  const own = Number(o[`advanced_v_${k}`] || 0);
  if (own > 0) return own;
  const any = Object.keys(o)
    .filter((key) => key.startsWith('advanced_v_'))
    .reduce((best, key) => Math.max(best, Number(o[key] || 0)), 0);
  return any || (o.advanced ? 1 : 0);
}
export function advancedDone(apiClient) {
  const o = (apiClient && apiClient.onboarding) || {};
  const k = emrKey(apiClient);
  const want = ADVANCED_VERSION[k] != null ? ADVANCED_VERSION[k] : ADVANCED_VERSION.default;
  return advancedFinishedAt(o, k) >= want;
}
// Something in Advanced features shipped since they last finished it?
export function advancedHasNew(apiClient) {
  const o = (apiClient && apiClient.onboarding) || {};
  const finishedAt = advancedFinishedAt(o, emrKey(apiClient));
  return finishedAt > 0 && !advancedDone(apiClient);
}

// Accounts created before the playground onboarding shipped never see it
// (unless `force_onboarding_checklist` is on for them). Friday 2026-09-18.
export const CHECKLIST_LAUNCH_DATE = new Date('2026-09-18T00:00:00Z');

export function onboardingChecklistActive(apiClient) {
  if (!apiClient || !apiClient.email) return false;
  if (/@demo\.comprehendpt\.ai$/i.test(apiClient.email)) return false;
  if (apiClient.isConfluentUser) return false;
  const forced = !!(apiClient.features && apiClient.features['force_onboarding_checklist']);
  if (!forced && apiClient.createdAt && new Date(apiClient.createdAt) < CHECKLIST_LAUNCH_DATE) return false;
  const o = apiClient.onboarding;
  if (!o) return false;
  if (o.dismissed) return false;
  const isCX = typeof window !== 'undefined' &&
    (window.location.protocol === 'chrome-extension:' || /(^|\.)embed\.comprehendpt\.com$/.test(window.location.hostname));
  const done = [true, basicDone(o), !!o.cx_authed || isCX, !!o.real, advancedDone(apiClient)].filter(Boolean).length;
  return done < 5;
}
